import { ColumnsType } from "antd/es/table";
import {
  ITelegramBotUser,
  TelegramBotConfig,
} from "@lib/types/TelegramChatBot";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import dayjs from "dayjs";
import { TruncateString } from "@lib/components/MiniComponents/Table/TruncateString";
import { GET_LANGUAGE_FROM_CODE } from "@lib/constants/Language";
import { Tag } from "antd";
import { TableColumnsProps } from "@lib/types/General";
import { Link } from "react-router-dom";
import { randomColor } from "@lib/helpers/ColorHelper";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { ROUTES } from "@lib/constants";
import { getRoute } from "@lib/helpers/RouteHelper";
import { ManagerName } from "@lib/components/MiniComponents/Others";

type Props<T extends ITelegramBotUser> = TableColumnsProps<T> & {
  telegramBotConfigs: TelegramBotConfig[];
};

const InteractedBots = ({
  bots,
  telegramBotConfigs = [],
}: {
  bots: string[];
  telegramBotConfigs: TelegramBotConfig[];
}): React.ReactNode => {
  return bots
    .map((botId) => telegramBotConfigs.find((bot) => bot.id === botId))
    .filter(Boolean)
    .map((bot, index) => (
      <Tag key={bot?.id} color={randomColor(index)}>
        <Link
          to={getRoute(ROUTES.ADMIN.TELEGRAM_BOT.TELEGRAM_BOT_CONFIG.LIST_TAB, {
            configId: bot?.id,
          })}
        >
          {bot?.name}
        </Link>
      </Tag>
    ));
};

const getColumns = <T extends ITelegramBotUser>(
  props: Props<T>,
): ColumnsType<T> => {
  const { telegramBotConfigs } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "Chat ID",
      dataIndex: "chatId",
      width: "160px",
      render: (value) => (
        <TableIdButton
          text={`${shortenString(value)}`}
          textToBeCopied={value}
        />
      ),
    },

    {
      title: "common.manager",
      dataIndex: "manager",
      width: "160px",
      render: (v, r) => (v ? <ManagerName manager={r?.manager} link /> : "N/A"),
    },

    {
      title: "common.telegramUsername",
      key: "username",
      dataIndex: "username",
      width: "170px",
      render: (username) => (
        <TruncateString str={username || "N/A"} maxLength={15} />
      ),
    },
    {
      title: "common.fullName",
      key: "fullName",
      dataIndex: "extraData",
      width: "160px",
      render: (value, { extraData }) => {
        const fullName =
          [extraData?.first_name, extraData?.last_name]
            .filter(Boolean)
            .join(" ") || "N/A";

        return <TruncateString str={fullName} maxLength={23} />;
      },
    },
    {
      title: "common.interactedBots",
      dataIndex: "interactedBots",
      width: "200px",
      dontSort: true,
      render: (_, { interactedBots = [] }) => {
        return interactedBots.length ? (
          <StyledFlex style={{ gap: "5px", flexWrap: "wrap" }}>
            <InteractedBots
              bots={interactedBots}
              telegramBotConfigs={telegramBotConfigs}
            />
          </StyledFlex>
        ) : (
          "N/A"
        );
      },
    },
    {
      title: "common.unAuthInteractedBots",
      dataIndex: "unAuthInteractedBots",
      width: "200px",
      dontSort: true,
      render: (_, { unAuthInteractedBots = [] }) => {
        return unAuthInteractedBots.length ? (
          <StyledFlex style={{ gap: "5px", flexWrap: "wrap" }}>
            <InteractedBots
              bots={unAuthInteractedBots}
              telegramBotConfigs={telegramBotConfigs}
            />
          </StyledFlex>
        ) : (
          "N/A"
        );
      },
    },
    {
      title: "common.language",
      dataIndex: "extraData.language_code",
      width: "160px",
      render: (value, { extraData }) => (
        <TruncateString
          str={GET_LANGUAGE_FROM_CODE[extraData?.language_code] || "N/A"}
          maxLength={10}
        />
      ),
    },
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "150px",
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      width: "150px",
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
